import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
// components
import { FormattedMessage } from 'react-intl';
import SegmentedTabs from 'components/SegmentedTabs';
import Preloader from 'components/Preloader';
import PigMovementsView from './components/PigMovementsView';
import TreatmentsView from './components/TreatmentsView';
// utils
import { getLSMyScheduleTab, setLSMyScheduleTab } from 'utils/localStorageHelper';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { myScheduleTabs } from 'utils/constants';
import mixpanel from 'mixpanel-browser';
// api
import { getMyScheduleCounts } from 'endpoints/mySchedule';

const MySchedule = ({ currentUserId, isLogisticsEnabled, isOnline, isTreatmentProtocolsEnabled, router }) => {
  const [counts, setCounts] = useState(null);
  const [activeTab, setActiveTab] = useState(
    getLSMyScheduleTab() === myScheduleTabs.treatments ? myScheduleTabs.treatments : myScheduleTabs.pigMovements
  );

  // Scheduling module may be off while treatment protocols are in use (and vice versa),
  // so counts also tell which views are available. When counts are unreachable, fall
  // back to the tenant flags instead of assuming the logistics view exists.
  const flagsFallbackCounts = {
    pig_movements: { enabled: isLogisticsEnabled, due_count: 0 },
    treatments: { enabled: isTreatmentProtocolsEnabled, due_count: 0 },
  };

  useEffect(() => {
    if (!isOnline) return;
    getMyScheduleCounts()
      .then(setCounts)
      .catch((errors) => {
        setCounts(flagsFallbackCounts);
        toastResponseErrors(errors);
      });
  }, [isOnline]);

  const effectiveCounts = counts || (!isOnline ? flagsFallbackCounts : null);

  const isPigMovementsEnabled = effectiveCounts?.pig_movements?.enabled;
  const isTreatmentsEnabled = effectiveCounts?.treatments?.enabled;
  const hasToggle = isPigMovementsEnabled && isTreatmentsEnabled;
  // single active view is the entire page; last visited tab wins when both are active.
  // With nothing active (e.g. TP flags on but no protocols yet) the flag-side view is shown empty.
  const getOnlyEnabledTab = () => {
    if (isTreatmentsEnabled) return myScheduleTabs.treatments;
    if (isPigMovementsEnabled) return myScheduleTabs.pigMovements;
    return isTreatmentProtocolsEnabled && !isLogisticsEnabled
      ? myScheduleTabs.treatments
      : myScheduleTabs.pigMovements;
  };
  const currentTab = hasToggle ? activeTab : getOnlyEnabledTab();

  // keep the last visited tab in sync even when it was auto-selected
  useEffect(() => {
    if (effectiveCounts) setLSMyScheduleTab(currentTab);
  }, [currentTab, effectiveCounts]);

  if (!effectiveCounts) return <Preloader isActive />;

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setLSMyScheduleTab(tab);
    mixpanel.track('Feature Used', {
      user_id: currentUserId,
      feature_name: 'MySchedule',
      action: 'Toggle Schedule Type',
      schedule_type: tab,
    });
  };

  const tabs = hasToggle ? (
    <SegmentedTabs
      options={[
        {
          value: myScheduleTabs.pigMovements,
          label: <FormattedMessage id="general.pigMovements" />,
          counter: effectiveCounts.pig_movements?.due_count,
        },
        {
          value: myScheduleTabs.treatments,
          label: <FormattedMessage id="general.treatments" />,
          counter: effectiveCounts.treatments?.due_count,
        },
      ]}
      value={currentTab}
      onChange={handleTabChange}
    />
  ) : null;

  if (currentTab === myScheduleTabs.treatments) {
    return <TreatmentsView tabs={tabs} />;
  }

  return <PigMovementsView tabs={tabs} router={router} />;
};

MySchedule.propTypes = {
  currentUserId: T.number,
  isLogisticsEnabled: T.bool,
  isOnline: T.bool.isRequired,
  isTreatmentProtocolsEnabled: T.bool,
  router: T.object.isRequired,
};

export default connect(
  (state) => ({
    currentUserId: state.auth.user?.id,
    isOnline: state.network.isOnline,
    isLogisticsEnabled: Boolean(state.auth.user?.current_company?.logistics),
    isTreatmentProtocolsEnabled: Boolean(state.auth.user?.current_company?.admin_treatment_protocols
      && state.auth.user?.current_company?.treatment_protocols),
  })
)(MySchedule);
